import { Component, inject, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { LoginPayload } from '../../models/auth.model';
import { UserRole } from '../../models/user.model';
import { Auth } from '../../services/auth';
import { ShellPathService } from '../../services/shell-path.service';
import { WithdrawalService } from '../../services/withdrawal.service';
import { decodeQrLoginPayload } from '../../utils/qr-login-payload.util';

@Component({
  selector: 'app-signin',
  imports: [
    ReactiveFormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    SelectModule,
    InputNumberModule,
    ButtonModule,
    InputIconModule,
    IconFieldModule,
    RouterLink,
  ],
  templateUrl: './signin.html',
  styleUrl: './signin.css',
  standalone: true,
})
export class Signin implements OnInit {
  passwordVisible = false;
  private authService = inject(Auth);
  private messageService = inject(MessageService);
  private shellPath = inject(ShellPathService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private withdrawalService = inject(WithdrawalService);

  openWithdrawalDialog(): void {
    this.withdrawalService.openDialog();
  }

  ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('verified') === '1') {
      this.messageService.add({
        severity: 'success',
        summary: $localize`Email verified`,
        detail: $localize`You can now sign in with your account.`,
      });
    }
    void this.applyAuthFromQueryParams();
  }

  private async applyAuthFromQueryParams(): Promise<void> {
    const params = await firstValueFrom(this.route.queryParams.pipe(take(1)));
    const authToken = params['auth'];
    if (!authToken) {
      return;
    }
    const payload = await decodeQrLoginPayload(
      authToken,
      environment.qrLoginSecret,
    );
    if (!payload) {
      return;
    }
    if (payload.username) {
      this.form.controls.username.setValue(payload.username);
    } else if (payload.email) {
      this.form.controls.username.setValue(payload.email);
    }
    if (payload.password) {
      this.form.controls.password.setValue(payload.password);
    }
    await this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { auth: null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  form = new FormGroup({
    username: new FormControl<string>('', [Validators.required]),
    password: new FormControl<string>('', [Validators.required]),
  });

  onLogin() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: $localize`Missing fields`,
        detail: $localize`Please fill in all required fields`,
      });
      return;
    }
    const payload: LoginPayload = {
      username: this.form.value.username!,
      password: this.form.value.password!,
    };
    this.authService
      .login(payload)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          localStorage.setItem('accessToken', response.tokens.accessToken);
          localStorage.setItem('refreshToken', response.tokens.refreshToken);
          const redirectUrl = this.route.snapshot.queryParamMap.get('redirect');
          if (redirectUrl) {
            this.router.navigateByUrl(redirectUrl);
          } else {
            if (
              !response.user.onboardingCompleted &&
              response.user.role === UserRole.USER
            ) {
              this.shellPath.navigateToRoute(['onboarding']);
              return;
            }
            this.shellPath.navigateHome();
          }
        },
        error: (err) => {
          const message = err?.error?.message || $localize`Invalid credentials`;
          this.messageService.add({
            severity: 'error',
            summary: $localize`Error`,
            detail: message,
          });
        },
      });
  }

  togglePasswordVisibility() {
    this.passwordVisible = !this.passwordVisible;
  }
}
